import {
  PERMISSION_MODULE_ROW_ORDER,
  PERMISSION_MODULE_SCOPES,
  PermissionModule,
  StaffPermissionRole,
  StaffScope,
} from '@constants/Settings/staffPermission.const'

export type PermissionsRoleColumnKey =
  | 'manager'
  | 'provider'
  | 'hygienist'
  | 'dentalAsst'
  | 'frontDesk'
  | 'accounting'

export const permissionRoleColumns: readonly PermissionsRoleColumnKey[] = [
  'manager',
  'provider',
  'hygienist',
  'dentalAsst',
  'frontDesk',
  'accounting',
]

export const EDITABLE_PERMISSION_ROLE_COLUMNS: readonly PermissionsRoleColumnKey[] =
  permissionRoleColumns

const ROLE_COLUMN_TO_WORK_ROLE: Record<
  PermissionsRoleColumnKey,
  StaffPermissionRole
> = {
  manager: StaffPermissionRole.MANAGER,
  provider: StaffPermissionRole.PROVIDER,
  hygienist: StaffPermissionRole.HYGIENIST,
  dentalAsst: StaffPermissionRole.DENTAL_ASSISTANT,
  frontDesk: StaffPermissionRole.FRONT_DESK,
  accounting: StaffPermissionRole.ACCOUNTING,
}

export type PermissionsMatrixRow = {
  id: string
  module: string
  manager: boolean
  provider: boolean
  hygienist: boolean
  dentalAsst: boolean
  frontDesk: boolean
  accounting: boolean
}

export type RolePermissionApiRow = {
  work_role: string
  scopes: string[]
}

const STAFF_MATRIX_ROLES = new Set<string>(
  EDITABLE_PERMISSION_ROLE_COLUMNS.map((col) => ROLE_COLUMN_TO_WORK_ROLE[col]),
)

export function rolePermissionsApiToRows(
  entries: RolePermissionApiRow[],
): PermissionsMatrixRow[] {
  const byRole = new Map<string, Set<string>>()
  for (const row of entries) {
    if (!STAFF_MATRIX_ROLES.has(row.work_role)) continue
    let set = byRole.get(row.work_role)
    if (!set) {
      set = new Set<string>()
      byRole.set(row.work_role, set)
    }
    for (const s of row.scopes ?? []) {
      if (typeof s === 'string') set.add(s)
    }
  }

  return PERMISSION_MODULE_ROW_ORDER.map((moduleLabel, idx) => {
    const required = PERMISSION_MODULE_SCOPES[moduleLabel]
    const base: PermissionsMatrixRow = {
      id: String(idx + 1),
      module: moduleLabel,
      manager: false,
      provider: false,
      hygienist: false,
      dentalAsst: false,
      frontDesk: false,
      accounting: false,
    }

    for (const col of permissionRoleColumns) {
      const workRole = ROLE_COLUMN_TO_WORK_ROLE[col]
      const scopes = byRole.get(workRole) ?? new Set<string>()
      base[col] = required.every((scope: StaffScope) => scopes.has(scope))
    }

    return base
  })
}

export function rowsToSaveRolesPayload(
  rows: PermissionsMatrixRow[],
): { work_role: StaffPermissionRole; scopes: StaffScope[] }[] {
  return EDITABLE_PERMISSION_ROLE_COLUMNS.map((col) => {
    const work_role = ROLE_COLUMN_TO_WORK_ROLE[col]
    const scopeSet = new Set<string>()
    for (const row of rows) {
      if (!row[col]) continue
      const moduleKey = row.module as PermissionModule
      const modScopes = PERMISSION_MODULE_SCOPES[moduleKey]
      if (!modScopes) continue
      modScopes.forEach((s) => scopeSet.add(s))
    }
    return {
      work_role,
      scopes: Array.from(scopeSet).sort() as StaffScope[],
    }
  })
}
